import React, { FC, HTMLAttributes, MouseEvent, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import { Button, Heading } from 'components/ui';
import { useOnClickOutside } from 'hooks';
import { t } from 'utils';
import Error from 'components/Error/Error';

import styles from './TooltipConfirm.module.scss';

export type Placement = 'right' | 'bottom' | 'left' | 'top';

export interface ITooltipConfirm extends HTMLAttributes<HTMLDivElement> {
  title: string;
  text: string;
  confirmText: string;
  placement?: Placement;
  onConfirm?: (e?: MouseEvent) => any;
}

const TooltipConfirm: FC<ITooltipConfirm> = (props) => {
  const { title, text, confirmText, placement, onConfirm = () => {}, className, children } = props;
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isShow, setIsShow] = useState(false);
  const [error, setError] = useState(null);
  const rootClassName = classNames(styles.root, className, {
    [styles.right]: placement === 'right',
    [styles.bottom]: placement === 'bottom',
    [styles.left]: placement === 'left',
  });
  const tooltipClassName = classNames(styles.tooltip, { 'd-none': !isShow });

  const handleCloseTooltip = useCallback(() => {
    setIsShow(false);
    setError(null);
  }, []);

  const handleConfirmClick = useCallback(
    async (e: MouseEvent) => {
      const result = onConfirm(e);
      const error = await result;
      // return error when using, because we might need to show an error
      if (!error) setIsShow(false);
      else setError(error);
    },
    [onConfirm, setError]
  );

  const handleChildrenContainerClick = useCallback(() => {
    setIsShow(true);
  }, []);

  const handleContainerClick = useCallback((e: MouseEvent) => {
    e.stopPropagation();
  }, []);

  useOnClickOutside(tooltipRef, handleCloseTooltip);

  return (
    <div className={rootClassName} onClick={handleContainerClick} role='presentation'>
      <div className={tooltipClassName} role='tooltip' ref={tooltipRef}>
        <Heading className={styles.heading} icon='check-double'>
          {t(title)}
        </Heading>
        {text && <p className={styles.text}>{t(text)}</p>}
        <Error className='mb-1' isError={!!error}>
          {error}
        </Error>
        <Button
          onClick={handleCloseTooltip}
          className='mr-2'
          type='button'
          variant='secondary'
          inline
        >
          {t('cancel')}
        </Button>
        <Button onClick={handleConfirmClick} type='button' inline>
          {t(confirmText)}
        </Button>
      </div>
      <div onClick={handleChildrenContainerClick} role='presentation'>
        {children}
      </div>
    </div>
  );
};

export default TooltipConfirm;
